import React from "react";
import { Link } from "react-router-dom";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "outline" | "ghost" | "success" | "destructive" | "inverse";
  size?: "sm" | "md" | "lg";
  asLink?: string;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = "primary",
  size = "md",
  className = "",
  asLink,
  ...props
}) => {
  const baseClasses =
    "inline-flex cursor-pointer items-center justify-center rounded-xl font-semibold transition-all focus:outline-none focus:ring-2 focus:ring-[var(--site-border-strong)] focus:ring-offset-2 focus:ring-offset-[var(--site-bg)] disabled:cursor-not-allowed disabled:opacity-50 disabled:pointer-events-none";

  const variants = {
    /* Primary: indigo accent, shadow (design-demo Get Started) */
    primary:
      "border-transparent bg-[var(--site-cta)] text-[var(--site-cta-contrast)] shadow-lg shadow-[color-mix(in_oklab,var(--site-cta)_30%,transparent)] hover:opacity-90",
    secondary:
      "border-transparent bg-[var(--site-surface-elevated)] text-[var(--site-text)] hover:bg-[var(--site-border)]",
    /* Outline: glass panel (design-demo View SDK Docs) */
    outline:
      "border border-[var(--site-border)] bg-[var(--site-glass)] backdrop-blur-[12px] text-[var(--site-text)] hover:bg-[var(--site-card)]",
    ghost:
      "border-transparent text-[var(--site-muted)] hover:text-[var(--site-text)] hover:bg-[var(--site-surface)]",
    success:
      "border-transparent bg-[var(--site-success)] text-[var(--site-success-contrast)] hover:opacity-90",
    destructive:
      "border border-[var(--site-destructive)]/50 bg-transparent text-[var(--site-destructive)] hover:bg-[var(--site-destructive)]/10",
    /* Inverse: white on dark (design-demo Launch Pilot) */
    inverse:
      "border-transparent bg-[var(--site-inverse)] text-[var(--site-inverse-contrast)] hover:scale-[1.02]",
  };

  const sizes = {
    sm: "h-9 px-4 text-sm",
    md: "h-10 px-5 py-2",
    lg: "h-12 px-8 text-base",
  };

  const classes = `${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`;

  if (asLink) {
    return (
      <Link to={asLink} className={classes}>
        {children}
      </Link>
    );
  }

  return (
    <button className={classes} {...props}>
      {children}
    </button>
  );
};
